/**
 * MMSE 솔루션 상세 페이지 — 스크리닝
 *
 * PO 원칙 준수:
 * - 원칙1: /30 점수 표시만. 결과 해석은 담당의 판단 — 자동 등급·판정 없음.
 * - 원칙4: "의료기관" 포괄 표현 사용.
 * - 원칙5: /30 표준 척도 명시.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';

const S: React.CSSProperties = { padding: '100px 0', borderTop: '1px solid var(--line)' };

function Page() {
  return (
    <PublicLayout>
      <SolutionLayout
        stage="scr"
        badgeLabel="SCR · 스크리닝"
        name="MMSE"
        tagline="5분 안에 끝나는 간이 인지 선별 검사. 의료기관 진료 흐름을 멈추지 않으면서 환자의 인지 저하 가능성을 빠르게 확인합니다."
        domain="스크리닝 · /30 표준 척도"
        crossSell={[
          {
            href: '/solutions/scst',
            name: 'SCST',
            desc: '신의료기술 통과 · 5개 인지 영역 정밀 평가 (30~40분)',
            stage: 'dx',
          },
          {
            href: '/solutions/pascal-med',
            name: 'PASCAL-Med',
            desc: '의료기관용 디지털 인지 중재 솔루션',
            stage: 'int',
          },
        ]}
        disclaimerItems={[
          {
            label: 'MMSE 사용 목적',
            text: 'MMSE는 인지 기능 1차 선별 목적의 도구이며, 단독으로 치매 진단에 사용되지 않습니다. /30점 척도 결과의 임상적 해석은 담당의의 종합 판단에 따릅니다.',
          },
          {
            label: '책임의 분리',
            text: '진료 행위 및 치료 결과에 대한 최종 책임은 담당의 및 해당 의료기관에 있습니다. BeauBrain은 의료기관에 도구를 공급하는 의료기기·서비스 사업자입니다.',
          },
        ]}
      >

        {/* ── 핵심 지표 ─────────────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock
              label="Key Metrics"
              title="한눈에 보는 MMSE"
              subtitle="진료실에서 가장 빠르게 시작할 수 있는 디지털 인지 선별 검사입니다."
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 56 }}>
                {[
                  { num: '5분', label: '검사 시간' },
                  { num: '/30', label: '표준 척도' },
                  { num: '즉시', label: '결과 확인' },
                ].map(({ num, label }) => (
                  <div key={label} className="b-item" style={{ textAlign: 'center', padding: '40px 32px' }}>
                    <div className="num" style={{ fontSize: 52, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 12 }}>
                      {num}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 3가지 임상 가치 ───────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock
              label="Why MMSE"
              title="의료기관의 첫 진단 단계로 적합합니다"
            >
              <div className="apple-bento" style={{ marginTop: 56 }}>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 01</span>
                  <div className="b-title">진료 흐름을 끊지 않습니다</div>
                  <p className="b-desc">
                    5분 내외의 검사 시간으로 일반 진료 흐름에 자연스럽게 통합됩니다.
                    환자가 태블릿으로 자기 주도적으로 진행하므로 의사·간호사의 직접 시간 투입이 최소화됩니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 02</span>
                  <div className="b-title">표준화된 디지털 변환</div>
                  <p className="b-desc">
                    국제적으로 검증된 MMSE 검사를 디지털로 표준화하여 채점 오류 없이 일관된
                    <strong> /30점 수치</strong>를 산출합니다.
                    결과 해석은 담당의 종합 판단에 따릅니다.
                  </p>
                </div>
                <div className="b-item">
                  <span className="b-eyebrow">VALUE 03</span>
                  <div className="b-title">다음 단계로 자연스럽게</div>
                  <p className="b-desc">
                    MMSE 결과에 따라 SCST(정밀 진단보조)나 PASCAL-Med(인지중재) 등
                    다음 진료 단계 솔루션의 정보를 안내합니다.
                    시행 여부와 시점은 담당의가 판단합니다.
                  </p>
                </div>
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 ─────────────────────────────────────────── */}
        <section style={S}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 16,
                  marginTop: 56,
                }}
              >
                {[
                  {
                    title: '디지털 표준화 (/30점)',
                    desc: '국제 MMSE 표준을 디지털로 구현. 채점 오류 없이 일관된 /30 점수를 산출합니다.',
                  },
                  {
                    title: '즉시 결과 확인',
                    desc: '검사 종료 즉시 /30 점수와 항목별 결과가 의사 대시보드에 표시됩니다. 결과 해석은 담당의 판단 영역입니다.',
                  },
                  {
                    title: '진료 기록 자동 보관',
                    desc: '결과가 BeauBrain 통합 계정에 자동 저장됩니다. 재방문 시 시기별 수치 변화를 확인할 수 있습니다.',
                  },
                  {
                    title: 'SCST 연동 안내',
                    desc: 'MMSE 결과를 바탕으로 SCST(정밀 진단보조) 정보를 안내합니다. 시행 결정은 담당의가 임상적으로 판단합니다.',
                  },
                  {
                    title: '환자 설명용 리포트',
                    desc: '의사용 + 환자/보호자용 일반인 리포트를 동시 생성합니다. 항목별 점수가 시각화되어 설명이 편리합니다.',
                  },
                  {
                    title: '간호사 보조 가능',
                    desc: '의사 직접 시행 외에도 간호사 보조로 운영 가능합니다. 진료 효율성을 높일 수 있습니다.',
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="b-item" style={{ display: 'flex', gap: 16, padding: '28px 24px' }}>
                    <div
                      style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: 'rgba(0,229,255,0.1)', color: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 700,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section style={{ ...S, background: 'var(--bg-surface)' }}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 760, margin: '56px auto 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  {
                    q: '기존 종이 MMSE와 어떻게 다른가요?',
                    a: '검사 내용은 동일하지만 디지털로 표준화되어 채점 오류가 없습니다. 결과가 자동 기록되어 재방문 시 시기별 점수 변화 추적이 가능합니다.',
                  },
                  {
                    q: 'MMSE만으로 치매 진단이 가능한가요?',
                    a: 'MMSE는 진단이 아닌 1차 선별 검사입니다. /30점 척도로 결과를 제공하며, 임상 해석은 담당의 종합 판단에 따릅니다. 정밀 평가가 필요한 경우 SCST(진단보조) 정보를 안내합니다.',
                  },
                  {
                    q: '환자에게 부담스럽지 않은가요?',
                    a: '5분 내외의 가벼운 검사로 환자 부담이 적습니다. 태블릿 사용이 익숙하지 않은 고령 환자도 간단한 안내로 진행 가능합니다.',
                  },
                  {
                    q: '가격은 어떻게 책정되나요?',
                    a: '통합 크레딧 안에서 회당 크레딧이 차감됩니다. 자세한 단가는 통합 크레딧 안내에서 확인하실 수 있습니다.',
                  },
                ].map(({ q, a }) => (
                  <div
                    key={q}
                    className="b-item"
                    style={{ padding: '24px 28px', borderRadius: 14 }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 10, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span className="num" style={{ color: 'var(--accent)', fontWeight: 800, flexShrink: 0 }}>Q.</span>
                      {q}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, paddingLeft: 28 }}>{a}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/mmse')({
  component: Page,
});
